<template>
  <div class="card card-height">
    <div class="card-body" :class="{ 'text-center': img }">
      <img v-if="img" class="mb-2" :src="img" />
      <div class="card-title title-style bold">{{ title }}</div>
      <div class="card-title value-style">{{ value }}</div>
      <div class="card-title metric-style">{{ metric }}</div>

      <div class="row">
        <div v-if="topIcon" class="col-3">
          <img :src="topIcon" />
        </div>
        <div class="col" :class="{ 'mb-2': topLinkAddress }">
          <div class="number-value-style">{{ topValue }}</div>
          <div class="metric-style">{{ topMetric }}</div>
        </div>
        <span v-if="topLink && !topLinkAddress" class="text-decoration-none">{{ topLink }}</span>

        <!-- Smart Top Link - Router or External -->
        <router-link
          v-if="topLinkAddress && isInternalRoute(topLinkAddress)"
          :to="topLinkAddress"
          class="metric-style black right-arrow-after text-decoration-none"
        >{{ topLink }}</router-link>
        <a
          v-else-if="topLinkAddress"
          target="_parent"
          :href="topLinkAddress"
          class="metric-style black right-arrow-after text-decoration-none"
        >{{ topLink }}</a>
      </div>

      <hr class="line-style" />

      <div>
        <span v-if="leftValue" class="float-start metric-style black">{{ leftValue }}</span>
        <span
          v-if="rightValue && !rightValueLink"
          class="float-end title-style hover-title right-arrow-after pe-none"
        >{{ rightValue }}</span>

        <!-- Smart Right Value Link - Router or External -->
        <router-link
          v-if="rightValueLink && isInternalRoute(rightValueLink)"
          :to="rightValueLink"
          class="float-end title-style hover-title right-arrow-after text-decoration-none"
        >{{ rightValue }}</router-link>
        <a
          v-else-if="rightValueLink"
          target="_parent"
          :href="rightValueLink"
          class="text-decoration-none"
        >
          <span class="float-end title-style hover-title right-arrow-after">{{ rightValue }}</span>
        </a>
      </div>

      <div class="row">
        <div v-if="bottomIcon" class="col-3">
          <img :src="bottomIcon" />
        </div>
        <div class="col mb-2">
          <div class="number-value-style">{{ bottomValue }}</div>
          <div class="metric-style">{{ bottomMetric }}</div>
        </div>
        <span v-if="bottomLink && !bottomLinkAddress" class="text-decoration-none">{{ bottomLink }}</span>

        <!-- Smart Bottom Link - Router or External -->
        <router-link
          v-if="bottomLinkAddress && isInternalRoute(bottomLinkAddress)"
          :to="bottomLinkAddress"
          class="metric-style black right-arrow-after text-decoration-none"
        >{{ bottomLink }}</router-link>
        <a
          v-else-if="bottomLinkAddress"
          target="_parent"
          :href="bottomLinkAddress"
          class="metric-style black right-arrow-after text-decoration-none"
        >{{ bottomLink }}</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    img: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    metric: {
      type: String,
      default: null,
    },
    leftValue: {
      type: [String, Number],
      default: null,
    },
    rightValue: {
      type: [String, Number],
      default: null,
    },
    rightValueLink: {
      type: [String, Object],
      default: null,
    },
    topIcon: {
      type: String,
      default: null,
    },
    topValue: {
      type: [String, Number],
      default: null,
    },
    topMetric: {
      type: String,
      default: null,
    },
    topLink: {
      type: String,
      default: null,
    },
    topLinkAddress: {
      type: [String, Object],
      default: null,
    },
    bottomIcon: {
      type: String,
      default: null,
    },
    bottomValue: {
      type: [String, Number],
      default: null,
    },
    bottomMetric: {
      type: String,
      default: null,
    },
    bottomLink: {
      type: String,
      default: null,
    },
    bottomLinkAddress: {
      type: [String, Object],
      default: null,
    },
    // New optional prop to force external link behavior
    forceExternal: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * Determines if a link should be treated as an internal Vue Router route
     * @param {string|object} link - The link to analyze
     * @returns {boolean} - True if should use router-link, false for external link
     */
    isInternalRoute(link) {
      // If forceExternal prop is true, always treat as external
      if (this.forceExternal) {
        return false;
      }

      // If no link provided, return false
      if (!link) {
        return false;
      }

      // If link is an object, it's a Vue Router route object
      if (typeof link === 'object') {
        return this.isValidRouteObject(link);
      }

      // If link is not a string at this point, return false
      if (typeof link !== 'string') {
        return false;
      }

      // External URL patterns (http, https, mailto, tel, etc.)
      const externalPatterns = [
        /^https?:\/\//i,           // http:// or https://
        /^mailto:/i,               // mailto:
        /^tel:/i,                  // tel:
        /^ftp:/i,                  // ftp:
        /^\/\//,                   // protocol-relative URLs
        /^#/,                      // hash links
        /\.(pdf|doc|docx|xls|xlsx|zip|rar)$/i, // file downloads
      ];

      // Check if link matches any external pattern
      const isExternal = externalPatterns.some(pattern => pattern.test(link));
      if (isExternal) {
        return false;
      }

      // Internal route patterns
      const internalPatterns = [
        /^\/[^/]/,                // Starts with single slash (absolute path)
        /^[a-zA-Z0-9]/,           // Starts with alphanumeric (relative path)
        /^\./,                     // Starts with dot (relative path)
      ];

      // Check if link matches internal pattern
      const isInternal = internalPatterns.some(pattern => pattern.test(link));

      // Additional check: if it's a route name
      if (this.isValidRouteName(link)) {
        return true;
      }

      return isInternal;
    },

    /**
     * Validates if an object is a valid Vue Router route object
     * @param {object} routeObj - The route object to validate
     * @returns {boolean} - True if it's a valid route object
     */
    isValidRouteObject(routeObj) {
      if (!routeObj || typeof routeObj !== 'object') {
        return false;
      }

      // A valid route object should have at least one of these properties
      const validRouteProps = ['name', 'path', 'params', 'query', 'hash'];
      const hasValidProp = validRouteProps.some(prop => Object.prototype.hasOwnProperty.call(routeObj, prop));

      // Additional validation: if it has a name or path, it's likely a route
      const hasNameOrPath = routeObj.name || routeObj.path;

      return hasValidProp && hasNameOrPath;
    },

    /**
     * Check if the link might be a valid route name
     * @param {string} link - The link to check
     * @returns {boolean} - True if it looks like a route name
     */
    isValidRouteName(link) {
      // Route names typically don't contain slashes, dots, or protocols
      return typeof link === 'string' &&
             !/[/.:]/.test(link) &&
             link.length > 0 &&
             !/^https?/i.test(link);
    }
  },
};
</script>

<style></style>
